import { useCallback, useMemo } from 'react';
import { registerMutation, useMutation } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { alertFailure, alertSuccess } from 'framework-listing-lib/utils/alerts';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useApplicationId from 'framework-listing-lib/internal/hooks/useApplicationId';
import { useViewsNamespace } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { convertSalesViewToView } from '../utils/mapper';
import { updatePinnedViewsCache, updateViewsCache } from '../utils/cache';
import { jsx as _jsx } from "react/jsx-runtime";
const UPDATE_VIEW_FIELD_NAME = 'updateViews';
const UPDATE_VIEW = registerMutation({
  fieldName: UPDATE_VIEW_FIELD_NAME,
  args: ['namespace', 'view'],
  fetcher({
    namespace,
    view
  }) {
    return http.put(`sales/v4/views/${view.id}`, {
      data: view,
      query: {
        namespace
      }
    });
  }
});
const UPDATE_DEFAULT_VIEW_FIELD_NAME = 'updateDefaultView';
const UPDATE_DEFAULT_VIEW = registerMutation({
  fieldName: UPDATE_DEFAULT_VIEW_FIELD_NAME,
  args: ['namespace', 'view'],
  fetcher({
    namespace,
    view
  }) {
    return http.put(`sales/v3/views/default-view`, {
      data: {
        columns: JSON.stringify(view.columns),
        id: view.id,
        objectTypeId: view.objectTypeId,
        state: JSON.stringify(view.state)
      },
      query: {
        namespace
      }
    }).then(() => view);
  }
});
export default function useMutateViews() {
  const namespace = useViewsNamespace();
  const objectType = useObjectTypeId();
  const applicationId = useApplicationId();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const [updateViewMutation, {
    loading: loadingUpdateView
  }] = useMutation(UPDATE_VIEW, {
    update(cache, {
      data: newData
    }) {
      const {
        [UPDATE_VIEW_FIELD_NAME]: salesView
      } = newData || {
        [UPDATE_VIEW_FIELD_NAME]: undefined
      };
      if (!salesView) {
        return;
      }
      const updatedView = convertSalesViewToView({
        namespace,
        salesView
      });
      const cacheUpdaterFn = viewsResponse => Object.assign({}, viewsResponse, {
        results: viewsResponse.results.map(cachedView => String(cachedView.id) === String(updatedView.id) ? updatedView : cachedView)
      });

      /**
       * Update current cache with new views
       */
      updateViewsCache({
        cache,
        variables: {
          namespace,
          objectType
        },
        updater: cacheUpdaterFn
      });

      /**
       * Update pinnedViews cache
       */
      updatePinnedViewsCache({
        cache,
        variables: {
          namespace,
          objectType
        },
        updater: cacheUpdaterFn
      });
    }
  });
  const [updateDefaultViewMutation, {
    loading: isSavingDefaultView
  }] = useMutation(UPDATE_DEFAULT_VIEW, {
    update(cache, {
      data: newData
    }) {
      const {
        [UPDATE_DEFAULT_VIEW_FIELD_NAME]: viewResponse
      } = newData || {
        [UPDATE_DEFAULT_VIEW_FIELD_NAME]: undefined
      };
      if (!viewResponse) {
        return;
      }
      const cacheUpdaterFn = viewsResponse => Object.assign({}, viewsResponse, {
        results: viewsResponse.results.map(cachedView => cachedView.id === viewResponse.id ? viewResponse : cachedView)
      });

      /**
       * Update current cache with new views
       */
      updateViewsCache({
        cache,
        variables: {
          namespace,
          objectType
        },
        updater: cacheUpdaterFn
      });

      /**
       * Update pinnedViews cache
       */
      updatePinnedViewsCache({
        cache,
        variables: {
          namespace,
          objectType
        },
        updater: cacheUpdaterFn
      });
    }
  });
  const saveView = useCallback(
  /**
   * Create new view if @view.id is not present.
   * Update view otherwise.
   */
  view => {
    const args = {
      variables: {
        namespace,
        view
      }
    };
    if (!view.id) {
      return Promise.resolve();
    }
    return updateViewMutation(args).then(response => {
      if (!response.data) {
        return undefined;
      }
      trackCrmTableInteraction({
        action: 'update-view',
        viewId: String(response.data.updateViews.id)
      });
      Metrics.counter('update-view-success-v4', {
        application_id: applicationId
      }).increment();
      const updatedView = convertSalesViewToView({
        namespace,
        salesView: response.data.updateViews
      });
      alertSuccess({
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "frameworkListingLib.viewTabs.alerts.viewUpdate.success.message",
          options: {
            name: updatedView.name
          }
        })
      });
      return updatedView;
    }).catch(() => {
      Metrics.counter('update-view-failure-v4', {
        application_id: applicationId
      }).increment();
      alertFailure({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.viewTabs.alerts.viewUpdate.failure.message"
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.viewTabs.alerts.viewUpdate.failure.title"
        })
      });
    });
  }, [applicationId, namespace, trackCrmTableInteraction, updateViewMutation]);
  const saveDefaultView = useCallback(view => {
    const args = {
      variables: {
        namespace,
        view
      }
    };
    return updateDefaultViewMutation(args).then(() => {
      Metrics.counter('update-default-view-success', {
        application_id: applicationId
      }).increment();
    }).catch(() => {
      Metrics.counter('update-default-view-failure', {
        application_id: applicationId
      }).increment();
    });
  }, [applicationId, namespace, updateDefaultViewMutation]);
  return useMemo(() => ({
    loading: loadingUpdateView || isSavingDefaultView,
    saveDefaultView,
    updateView: saveView
  }), [isSavingDefaultView, loadingUpdateView, saveDefaultView, saveView]);
}